import React from 'react';
import { cn } from '@/utils/cn';

export interface TeeStatusBadgeProps {
  status: string;
  className?: string;
  size?: 's' | 'm' | 'l';
}

export const TeeStatusBadge = React.forwardRef<
  HTMLDivElement,
  TeeStatusBadgeProps
>(({ status, className, size = 's' }, ref) => {
  const normalizedStatus = status?.toLowerCase();

  type StatusVariant =
    | 'active'
    | 'inactive'
    | 'pending'
    | 'new'
    | 'error'
    | 'presale'
    | 'default';

  const getStatusVariant = (status: string): StatusVariant => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'connected':
        return 'active';
      case 'inactive':
      case 'idle':
      case 'not connected':
      case 'disconnected':
        return 'inactive';
      case 'cancelled':
      case 'deleted':
      case 'expired':
      case 'payment_failed':
      case 'pending_deletion':
      case 'suspended':
        return 'error';
      case 'paused':
      case 'past_due':
      case 'pending':
      case 'processing':
      case 'draft_invoice':
        return 'pending';
      case 'presale':
        return 'presale';
      case 'trial':
        return 'pending';
      case 'new':
      case 'hq_review':
        return 'new';
      case 'invoiced':
        return 'active';
      case 'paid':
        return 'active';
      case 'partially_paid':
        return 'pending';
      case 'overdue':
      case 'invoice_deleted':
        return 'error';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    return status
      ?.split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const statusVariant = getStatusVariant(normalizedStatus);

  const statusStyles: Record<
    StatusVariant,
    { bg: string; text: string; border: string }
  > = {
    active: {
      bg: 'bg-[var(--background-badge-green)]',
      text: 'text-[var(--text-badge-green)]',
      border: 'border border-[rgba(12,61,45,0.20)]',
    },
    inactive: {
      bg: 'bg-[var(--background-badge-gray)]',
      text: 'text-[var(--text-badge-gray)]',
      border: 'border border-[rgba(22,36,30,0.12)]',
    },
    new: {
      bg: 'bg-[var(--color-gold-subtle)]',
      text: 'text-[var(--color-text-gold)]',
      border: 'border border-[var(--color-gold-border)]',
    },
    pending: {
      bg: 'bg-[var(--background-badge-amber)]',
      text: 'text-[var(--text-badge-warning)]',
      border: 'border border-[var(--color-gold-border)]',
    },
    presale: {
      bg: 'bg-[var(--background-badge-green)]',
      text: 'text-[var(--text-badge-green)]',
      border: 'border border-[rgba(12,61,45,0.20)]',
    },
    error: {
      bg: 'bg-[var(--background-badge-red)]',
      text: 'text-[var(--text-badge-red)]',
      border: 'border border-[rgba(146,52,48,0.25)]',
    },
    default: {
      bg: 'bg-[var(--background-badge-gray)]',
      text: 'text-[var(--text-badge-gray)]',
      border: 'border border-[rgba(22,36,30,0.12)]',
    },
  };

  const {
    bg: backgroundClasses,
    text: textClasses,
    border: borderClasses,
  } = statusStyles[statusVariant];

  const sizeClasses = {
    s: 'px-[var(--spacing-padding-margin-2xs,8px)] py-[var(--spacing-padding-margin-6xs,4px)] gap-[4px]',
    m: 'px-[var(--spacing-padding-margin-xs,10px)] py-[var(--spacing-padding-margin-6xs,4px)] gap-[4px]',
    l: 'px-[var(--spacing-padding-margin-3xs,10px)] py-[var(--spacing-padding-margin-6xs,4px)] gap-[4px]',
  };

  const fontClasses = {
    s: 'text-[length:var(--font-size-body-2xs,10px)] leading-[var(--line-height-body-2xs,14px)]',
    m: 'text-[length:var(--font-size-body-xs,12px)] leading-[var(--line-height-body-xs,16px)]',
    l: 'text-[length:var(--font-size-body-s,14px)] leading-[var(--line-height-body-s,22px)]',
  };

  return (
    <div
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-[100px] font-[family-name:var(--type-font-family-body,Inter)] font-[var(--type-font-weight-regular,400)]',
        sizeClasses[size],
        fontClasses[size],
        backgroundClasses,
        textClasses,
        borderClasses,
        className
      )}
    >
      {getStatusText(status)}
    </div>
  );
});

TeeStatusBadge.displayName = 'TeeStatusBadge';

export default TeeStatusBadge;
